import { DeepReadonly, readonly, Ref, ref, watch } from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import { Action } from '@mobile/ActionQueue/Action'
import { ActionQueue } from '@mobile/ActionQueue/ActionQueue'
import { isIncidentEditable } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { EventType } from '@mobile/ActionQueue/EventType'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { EventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import {
  correlateVIPDockWithAddAction,
  DockedIncidentCorrelation,
  findPendingVIPDockIncident,
  PendingVIPIncidentDock,
  resolveSelectedIncidentAfterSync,
} from './historyIncidentsDock.utils'
import { requestDockFocusOnMainSelect } from './useDockedIncidentComponent'

export type HistoryIncidentsReturnType = {
  selectIncident: (
    probableLinkedIncident: ProbableLinkedIncident | null,
  ) => void
  isIncidentSelected: (
    probableLinkedIncident: ProbableLinkedIncident,
  ) => boolean
  toggleIncidentPinState: (
    probableLinkedIncident: ProbableLinkedIncident,
  ) => void
  requestVIPIncidentDock: (
    incidentId: IncidentId,
    participantId: number,
  ) => void
  selectedIncident: DeepReadonly<Ref<ProbableLinkedIncident | null>>
  pinnedIncidentIds: Ref<string[]>
  isIncidentPinned: (probableLinkedIncident: ProbableLinkedIncident) => boolean
  clearSelection: () => void
  historyPageResetNonce: DeepReadonly<Ref<number>>
  historyPageDockIncidentId: DeepReadonly<Ref<string | null>>
}

const pinnedIncidentIds: Ref<string[]> = ref([])
const _selectedIncident: Ref<ProbableLinkedIncident | null> = ref(null)
const pendingVIPIncidentDock: Ref<PendingVIPIncidentDock | null> = ref(null)
const dockedIncidentCorrelation: Ref<DockedIncidentCorrelation | null> =
  ref(null)
const historyPageResetNonce = ref(0)
const historyPageDockIncidentId: Ref<string | null> = ref(null)

let stopLinkedIncidentsWatch: (() => void) | null = null
let watchedLinkedProbableIncidents:
  EventInjections['linkedProbableIncidents'] | null = null
let watchedSport: EventInjections['sport'] | null = null
let watchedEventId: number | null = null
let actionQueueSubscription: {
  queue: ActionQueue
  cleanup: () => void
} | null = null

function clearPendingVIPIncidentDock(): void {
  pendingVIPIncidentDock.value = null
}

function resetVIPDockStateOnEventChange(): void {
  clearPendingVIPIncidentDock()
  _selectedIncident.value = null
  dockedIncidentCorrelation.value = null
  historyPageDockIncidentId.value = null
}

function isMatchingErroredVIPDockAction(
  action: Action<unknown>,
  pending: PendingVIPIncidentDock,
): boolean {
  if (!pending.actionId) {
    return false
  }

  return action.id === pending.actionId
}

function ensureActionQueueListeners(actionQueue: Ref<ActionQueue>): void {
  if (actionQueueSubscription?.queue === actionQueue.value) {
    return
  }

  actionQueueSubscription?.cleanup()

  const queue = actionQueue.value

  const onActionErrored = (action: Action<unknown>): void => {
    const pending = pendingVIPIncidentDock.value
    if (!pending || !isMatchingErroredVIPDockAction(action, pending)) {
      return
    }

    clearPendingVIPIncidentDock()
  }

  queue.on(EventType.ActionErrored, onActionErrored)

  actionQueueSubscription = {
    queue,
    cleanup: () => {
      queue.off(EventType.ActionErrored, onActionErrored)
    },
  }
}

function handleLinkedProbableIncidentsUpdate(
  newProbableIncidents: ProbableLinkedIncident[],
  sport: EventInjections['sport'],
  eventId: number,
): void {
  tryDockPendingVIPIncident(newProbableIncidents, sport, eventId)

  const selected = _selectedIncident.value
  if (!selected) {
    return
  }

  const resolved = resolveSelectedIncidentAfterSync(
    newProbableIncidents,
    selected,
    dockedIncidentCorrelation.value,
  )

  if (!resolved) {
    const hasMatchingIncident = newProbableIncidents.some(
      (linkedIncident) =>
        linkedIncident.incident.incident_id === selected.incident.incident_id &&
        linkedIncident.participant?.id === selected.participant?.id,
    )

    if (!hasMatchingIncident) {
      _selectedIncident.value = null
      dockedIncidentCorrelation.value = null
    }

    return
  }

  _selectedIncident.value = resolved
  dockedIncidentCorrelation.value = {
    actionId: resolved.incident.id,
    queueUuid:
      resolved.uuid ?? dockedIncidentCorrelation.value?.queueUuid ?? null,
  }
}

function ensureLinkedProbableIncidentsWatch(
  eventInjections: EventInjections,
): void {
  const { linkedProbableIncidents, sport, probableEvent } = eventInjections
  const eventId = probableEvent.value.id

  if (watchedEventId !== null && watchedEventId !== eventId) {
    resetVIPDockStateOnEventChange()
  }

  watchedEventId = eventId

  if (
    watchedLinkedProbableIncidents === linkedProbableIncidents &&
    watchedSport === sport
  ) {
    return
  }

  stopLinkedIncidentsWatch?.()
  watchedLinkedProbableIncidents = linkedProbableIncidents
  watchedSport = sport

  stopLinkedIncidentsWatch = watch(
    linkedProbableIncidents,
    (newProbableIncidents) => {
      handleLinkedProbableIncidentsUpdate(
        newProbableIncidents,
        sport,
        probableEvent.value.id,
      )
    },
    { flush: 'sync' },
  )
}

function tryDockPendingVIPIncident(
  incidents: ProbableLinkedIncident[],
  sport: EventInjections['sport'],
  eventId: number,
): void {
  const pending = pendingVIPIncidentDock.value
  if (!pending || pending.eventId !== eventId) {
    return
  }

  const newIncident = findPendingVIPDockIncident(incidents, pending)
  if (!newIncident) {
    return
  }

  clearPendingVIPIncidentDock()
  dockedIncidentCorrelation.value = {
    actionId: pending.actionId,
    queueUuid: pending.queueUuid,
  }
  selectIncident(newIncident, sport)
  requestDockFocusOnMainSelect()
  historyPageDockIncidentId.value = newIncident.incident.id
  historyPageResetNonce.value++
}

function selectIncident(
  probableLinkedIncident: ProbableLinkedIncident | null,
  sport: EventInjections['sport'],
): void {
  if (
    probableLinkedIncident &&
    !isIncidentEditable(probableLinkedIncident.incident, sport)
  ) {
    return
  }

  _selectedIncident.value = probableLinkedIncident
}

export function useHistoryIncidents(
  eventInjections: EventInjections,
): HistoryIncidentsReturnType {
  const { sport, linkedProbableIncidents, actionQueue, probableEvent } =
    eventInjections

  ensureLinkedProbableIncidentsWatch(eventInjections)
  ensureActionQueueListeners(actionQueue)

  function requestVIPIncidentDock(
    incidentId: IncidentId,
    participantId: number,
  ): void {
    const eventId = probableEvent.value.id
    const { actionId, queueUuid } = correlateVIPDockWithAddAction(
      actionQueue.value,
      incidentId,
      participantId,
    )

    pendingVIPIncidentDock.value = {
      eventId,
      incidentId,
      participantId,
      existingIncidentIds: new Set(
        linkedProbableIncidents.value.map((incident) => incident.incident.id),
      ),
      actionId,
      queueUuid,
    }
    tryDockPendingVIPIncident(linkedProbableIncidents.value, sport, eventId)
  }

  function toggleIncidentPinState(
    probableLinkedIncident: ProbableLinkedIncident,
  ): void {
    if (!isIncidentEditable(probableLinkedIncident.incident, sport)) {
      return
    }

    const index = pinnedIncidentIds.value.findIndex(
      (pinnedIncidentId) =>
        pinnedIncidentId === probableLinkedIncident.incident.id,
    )

    if (index !== -1) {
      pinnedIncidentIds.value.splice(index, 1)
    } else {
      pinnedIncidentIds.value.unshift(probableLinkedIncident.incident.id)
    }
  }

  function isIncidentPinned(
    probableLinkedIncident: ProbableLinkedIncident,
  ): boolean {
    return !!pinnedIncidentIds.value.find(
      (pinnedIncidentId) =>
        pinnedIncidentId === probableLinkedIncident.incident.id,
    )
  }

  function clearSelection(): void {
    _selectedIncident.value = null
    dockedIncidentCorrelation.value = null
    historyPageDockIncidentId.value = null
    clearPendingVIPIncidentDock()
  }

  function isIncidentSelected(
    probableLinkedIncident: ProbableLinkedIncident,
  ): boolean {
    return (
      _selectedIncident.value?.incident.id ===
      probableLinkedIncident.incident.id
    )
  }

  return {
    selectIncident: (probableLinkedIncident) =>
      selectIncident(probableLinkedIncident, sport),
    clearSelection,
    isIncidentSelected,
    toggleIncidentPinState,
    requestVIPIncidentDock,
    selectedIncident: readonly(_selectedIncident),
    pinnedIncidentIds,
    isIncidentPinned,
    historyPageResetNonce: readonly(historyPageResetNonce),
    historyPageDockIncidentId: readonly(historyPageDockIncidentId),
  }
}
